import { Signal } from "../core/Signal.js";
import { Resize } from "../display/Resize.js";
import { Debug } from "./DebugView.js";
import { object } from "./ObjectUtil.js";

export interface DebugViewWindowOptions {
    html?: string;
    visible?: boolean;
    onInit?: (win: DebugViewWindow) => void;
    onResizeWidth?: (win: DebugViewWindow) => void;
    onShow?: (win: DebugViewWindow) => void;
    onHide?: (win: DebugViewWindow) => void;
    onVisibilityChange?: (win: DebugViewWindow) => void;
}

export class DebugViewWindow {
    public readonly onInit = Signal.create<[DebugViewWindow]>();
    public readonly onResizeWidth = Signal.create<[DebugViewWindow]>();
    public readonly onShow = Signal.create<[DebugViewWindow]>();
    public readonly onHide = Signal.create<[DebugViewWindow]>();

    public readonly element: HTMLElement;
    public readonly body: HTMLElement;

    private _initialized: boolean = false;
    private _width: number = 0;

    constructor(options: DebugViewWindowOptions) {
        options = object.merge({
            visible: false,
            html: ""
        }, options);

        this.element = document.createElement("div");
        this.element.className = "e5-debug-view-window";
        this.element.innerHTML = `
        <div class="e5-debug-view-window__header"></div>
        <div class="e5-debug-view-window__body"></div>
        <div class="e5-debug-view-window__footer"></div>
        `;

        this.body = this.element.querySelector(".e5-debug-view-window__body");

        this.html = options.html;
        this.visible = options.visible;

        const resizer = Resize.create(this.element);
        resizer.add(() => {
            this.checkForResize();
        });

        if (typeof options.onInit === "function") {
            this.onInit.add(options.onInit);
        }

        if (typeof options.onResizeWidth === "function") {
            this.onResizeWidth.add(options.onResizeWidth);
        }

        if (typeof options.onShow === "function") {
            this.onShow.add(options.onShow);
        }

        if (typeof options.onHide === "function") {
            this.onHide.add(options.onHide);
        }

        if (typeof options.onVisibilityChange === "function") {
            this.onShow.add(options.onVisibilityChange);
            this.onHide.add(options.onVisibilityChange);
        }

        Debug.onVisibilityChange.add(() => {
            this.applyInit();
        });

        this.applyInit();
    }

    public get html(): string {
        return this.element.querySelector(".e5-debug-view-window__body")
            .textContent;
    }

    public set html(value: string) {
        if (this.html == value) {
            return;
        }
        const html = this.element.querySelector(".e5-debug-view-window__body");
        html.innerHTML = value || "";
        html.classList.toggle("e5-hidden", !value);
    }

    public get visible(): boolean {
        return !this.element.classList.contains("e5-hidden");
    }

    public set visible(value: boolean) {
        if (this.visible === value) {
            return;
        }
        this.element.classList.toggle("e5-hidden", !value);

        this.applyInit();

        if (value) {
            this.onShow.dispatch(this);
            this.checkForResize();
        } else {
            this.onHide.dispatch(this);
        }
    }

    private applyInit(): boolean {
        if (
            !this._initialized
            && this.isVisible()
        ) {
            this._width = this.element.offsetWidth;
            this._initialized = true;
            this.onInit.dispatch(this);
            return true;
        }
        return false;
    }

    private checkForResize(): void {
        if (!this.isVisible()) {
            return;
        }
        const w = this.element.offsetWidth;
        if (w != this._width) {
            this._width = w;
            this.onResizeWidth.dispatch(this);
        }
    }

    private isVisible(): boolean {
        return this.visible
            && this.element.offsetWidth > 0
            && Debug.visible
            && !Debug.collapsed;
    }
}